import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { BookOpen, Download, Tag, ArrowRight, Star } from "lucide-react";

export default function Library() {
  const [items, setItems] = useState(null);

  useSEO({
    title: "Boxing PDF Library — Cheatsheets, Drills & E-books | Better Through Boxing",
    description: "Free boxing cheatsheets and downloadable lesson guides from Coach Chris Persaud. Stance, footwork, drills, conditioning, and the Flow State Method curriculum — distilled into PDFs you can train with.",
    path: "/library",
    keywords: [
      "boxing PDF", "boxing cheatsheet", "free boxing guide", "boxing drills PDF",
      "Flow State Method library", "Better Through Boxing downloads", "Coach Chris boxing ebook",
    ],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/library");
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="library-page">
      <SiteHeader />

      <section className="border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">PDF Library</p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white uppercase tracking-tight leading-[0.9]" data-testid="library-h1">
            Boxing.<br /><span className="text-[#FF331F]">Downloaded.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed">
            Cheatsheets, drill libraries, and full guides Coach Chris hands out to private clients.
            Free downloads in exchange for an email. Paid ones go deeper.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {items === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-[#27272A] aspect-[4/5] animate-pulse bg-[#0F0F0F]" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#27272A] rounded-sm p-16 text-center">
            <BookOpen className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">Library is loading new releases. Check back soon.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="library-grid">
            {items.map((it) => (
              <LibraryCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">01 — Pick</div>
            <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">Browse the library</h3>
            <p className="text-sm text-zinc-400">Each PDF targets a specific skill or scenario — at-home drills, fundamentals, conditioning, defense.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">02 — Capture</div>
            <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">Quick info, instant download</h3>
            <p className="text-sm text-zinc-400">Drop your name, email, and one line about your goal. The download fires immediately to your inbox.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">03 — Train</div>
            <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">Print it, use it, repeat</h3>
            <p className="text-sm text-zinc-400">Every PDF is built to be screenshotted, taped to your wall, or printed and brought to your home setup.</p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function LibraryCard({ item }) {
  const isFree = item.is_currently_free;
  const price = item.price_cents > 0 ? `$${(item.price_cents / 100).toFixed(item.price_cents % 100 === 0 ? 0 : 2)}` : "Free";
  return (
    <Link
      to={`/library/${item.slug}`}
      className="group block border border-[#27272A] hover:border-[#FF331F] bg-[#0A0A0A] transition-colors overflow-hidden"
      data-testid={`library-card-${item.slug}`}
    >
      <div className="aspect-[4/3] bg-[#050505] relative overflow-hidden">
        {item.cover_image ? (
          <img src={item.cover_image} alt={item.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"><BookOpen className="h-12 w-12 text-zinc-700" /></div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${isFree ? "bg-[#FF331F] text-white" : "bg-white text-black"}`}>
            {price}
          </span>
          {item.is_featured_magnet && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-amber-400 text-black rounded-sm inline-flex items-center gap-1">
              <Star className="h-3 w-3" /> Featured
            </span>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="h-3 w-3 text-zinc-500" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{item.category || "Guide"}</span>
        </div>
        <h3 className="font-display text-xl text-white uppercase tracking-tight leading-tight mb-2 group-hover:text-[#FF331F] transition-colors">{item.title}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed line-clamp-2 mb-4">{item.description}</p>
        <div className="text-[11px] uppercase tracking-widest text-[#FF331F] font-bold inline-flex items-center gap-1">
          {isFree ? <><Download className="h-3 w-3" /> Get the PDF</> : <>View details <ArrowRight className="h-3 w-3" /></>}
        </div>
      </div>
    </Link>
  );
}
